/*
  Nature Lane Beauty — header behaviour.

  Custom build #1 (compact-on-scroll) plus the mobile drawer, the desktop search
  panel, and keyboard/pointer handling for the nav dropdowns.

  No dependencies. Everything degrades to a usable header without JS: the nav is
  plain links, the dropdowns are native <details>, and the drawer's contents are
  reachable through the same menu.
*/
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  class NlbHeader extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('.nlb-header__bar');
      this.drawer = this.querySelector('.nlb-drawer');
      this.searchPanel = this.querySelector('.nlb-header__search');
      this.searchToggle = this.querySelector('[data-nlb-search-toggle]');
      this.drawerToggle = this.querySelector('[data-nlb-drawer-open]');
      this.drawerClose = this.querySelector('[data-nlb-drawer-close]');
      this.dropdowns = Array.prototype.slice.call(this.querySelectorAll('[data-nlb-dropdown]'));

      this.compactAt = parseInt(this.dataset.compactAt, 10);
      if (isNaN(this.compactAt)) this.compactAt = 40;

      this.isCompact = false;
      this.scrollQueued = false;
      this.lastFocused = null;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);

      this.bindSearch();
      this.bindDrawer();
      this.bindDropdowns();

      this.publishHeight();
      this.applyCompact(window.scrollY > this.compactAt);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      document.documentElement.classList.remove('nlb-drawer-open');
    }

    /* ---- Compact on scroll --------------------------------------------- */

    onScroll() {
      if (this.scrollQueued) return;
      this.scrollQueued = true;
      window.requestAnimationFrame(() => {
        this.scrollQueued = false;
        this.applyCompact(window.scrollY > this.compactAt);
      });
    }

    applyCompact(shouldCompact) {
      if (shouldCompact === this.isCompact) return;
      this.isCompact = shouldCompact;
      this.classList.toggle('is-compact', shouldCompact);

      // The height transition is 240ms; read after it settles so the published
      // value matches what is actually on screen.
      var delay = reduceMotion.matches ? 0 : 260;
      window.clearTimeout(this.heightTimer);
      this.heightTimer = window.setTimeout(() => this.publishHeight(), delay);
    }

    onResize() {
      this.publishHeight();
    }

    /*
      Exposed for anything that has to clear the sticky header — anchor offsets
      and, later, the sticky mobile add-to-cart bar (custom build #2).
    */
    publishHeight() {
      if (!this.bar) return;
      var height = Math.round(this.bar.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--nlb-header-height', height + 'px');
    }

    /* ---- Search panel --------------------------------------------------- */

    bindSearch() {
      if (!this.searchToggle || !this.searchPanel) return;
      this.searchToggle.addEventListener('click', () => {
        this.toggleSearch(this.searchPanel.hasAttribute('hidden'));
      });
    }

    toggleSearch(open) {
      if (!this.searchToggle || !this.searchPanel) return;
      this.searchPanel.toggleAttribute('hidden', !open);
      this.searchToggle.setAttribute('aria-expanded', String(open));
      this.publishHeight();

      if (open) {
        var input = this.searchPanel.querySelector('input[type="search"]');
        if (input) input.focus();
      } else {
        this.searchToggle.focus();
      }
    }

    /* ---- Mobile drawer -------------------------------------------------- */

    bindDrawer() {
      if (!this.drawer) return;
      if (this.drawerToggle) {
        this.drawerToggle.addEventListener('click', () => this.openDrawer());
      }
      if (this.drawerClose) {
        this.drawerClose.addEventListener('click', () => this.closeDrawer());
      }
      this.setDrawerInert(true);
    }

    openDrawer() {
      if (!this.drawer) return;
      this.lastFocused = document.activeElement;
      this.drawer.classList.add('is-open');
      this.setDrawerInert(false);
      if (this.drawerToggle) this.drawerToggle.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('nlb-drawer-open');

      var target = this.drawerClose || this.drawer.querySelector(FOCUSABLE);
      if (target) target.focus();
    }

    closeDrawer() {
      if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
      this.drawer.classList.remove('is-open');
      this.setDrawerInert(true);
      if (this.drawerToggle) this.drawerToggle.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('nlb-drawer-open');

      if (this.lastFocused && document.contains(this.lastFocused)) {
        this.lastFocused.focus();
      } else if (this.drawerToggle) {
        this.drawerToggle.focus();
      }
      this.lastFocused = null;
    }

    // CSS visibility already takes it out of the tab order; inert makes that
    // explicit for assistive tech where it is supported.
    setDrawerInert(inert) {
      if (!this.drawer || !('inert' in HTMLElement.prototype)) return;
      this.drawer.inert = inert;
    }

    trapFocus(event) {
      var focusable = Array.prototype.filter.call(
        this.drawer.querySelectorAll(FOCUSABLE),
        (el) => el.offsetParent !== null
      );
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ---- Dropdowns ------------------------------------------------------ */

    bindDropdowns() {
      this.dropdowns.forEach((details) => {
        // Opening one closes the others, so two panels never overlap.
        details.addEventListener('toggle', () => {
          if (!details.open) return;
          this.dropdowns.forEach((other) => {
            if (other !== details) other.open = false;
          });
        });

        // Hover-to-open on pointing devices only. On touch, the summary's own
        // click handles it and a hover binding would fire a phantom open.
        details.addEventListener('mouseenter', () => {
          if (finePointer.matches) details.open = true;
        });
        details.addEventListener('mouseleave', () => {
          if (finePointer.matches) details.open = false;
        });

        // Tabbing out of the panel closes it.
        details.addEventListener('focusout', (event) => {
          if (!details.contains(event.relatedTarget)) details.open = false;
        });
      });
    }

    closeDropdowns() {
      this.dropdowns.forEach((details) => {
        details.open = false;
      });
    }

    /* ---- Global handlers ------------------------------------------------ */

    onKeydown(event) {
      var drawerOpen = this.drawer && this.drawer.classList.contains('is-open');

      if (event.key === 'Escape') {
        if (drawerOpen) {
          this.closeDrawer();
        } else if (this.searchPanel && !this.searchPanel.hasAttribute('hidden')) {
          this.toggleSearch(false);
        } else {
          this.closeDropdowns();
        }
        return;
      }

      if (event.key === 'Tab' && drawerOpen) {
        this.trapFocus(event);
      }
    }

    onDocumentPointerDown(event) {
      if (this.contains(event.target)) return;
      this.closeDropdowns();
      if (this.searchPanel && !this.searchPanel.hasAttribute('hidden')) {
        this.searchPanel.setAttribute('hidden', '');
        if (this.searchToggle) this.searchToggle.setAttribute('aria-expanded', 'false');
        this.publishHeight();
      }
    }
  }

  if (!customElements.get('nlb-header')) {
    customElements.define('nlb-header', NlbHeader);
  }
})();
